import type {
  ColumnMapping,
  CsvImportError,
  ValueTransformMap,
  ValueTransforms,
} from "../types/customCsvImport";
import {
  labelForDatetimeFormat,
  resolveDatetimeFormatFix,
} from "./datetimeFormatInference";
import { isTypedTransform } from "./customCsvImport";

export type EnrichedPreviewError = {
  row: number | null;
  tesseraField: string;
  tesseraFieldLabel: string;
  columnHeader: string;
  rawValue: string;
  serverField: string | null;
  serverMessage: string;
  errorCode?: string | null;
  expected?: string | null;
  issue: string;
  fixHint?: string;
  /** True when displayed from precise server fields (no client-side guess). */
  fromServer?: boolean;
};

export type PreviewErrorSummary = {
  key: string;
  count: number;
  tesseraField: string;
  tesseraFieldLabel: string;
  issue: string;
  fixHint?: string;
  exampleRow: number;
  exampleColumn: string;
  exampleValue: string;
};

const TESSERA_FIELD_LABELS: Record<string, string> = {
  timestamp: "Date and time",
  price: "Price",
  quantity: "Quantity",
  side: "Side",
  symbol: "Symbol",
  execution_id: "Execution ID",
  trade_id: "Trade ID",
  row_kind: "Row type",
  position_effect: "Open / close",
  fee: "Fee",
  pnl: "P/L",
};

const VAGUE_ROW_VALUE_ERROR_RE =
  /invalid timestamp, price, or quantity|invalid quantity or price|could not parse/i;

/** Fields checked when the server omits `field` on a row-level parse error. */
const ROW_PARSE_FIELDS = ["timestamp", "price", "quantity", "side"] as const;

function labelForTesseraField(field: string): string {
  return TESSERA_FIELD_LABELS[field] ?? field.replace(/_/g, " ");
}

function getSideMap(transforms: ValueTransforms): ValueTransformMap {
  const raw = transforms.side;
  if (raw && !isTypedTransform(raw) && typeof raw === "object") {
    return raw as ValueTransformMap;
  }
  return {};
}

export function diagnoseTimestampValue(
  rawValue: string,
  datetimeFormat: string | undefined
): { ok: boolean; issue?: string; fixHint?: string } {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return {
      ok: false,
      issue: "Timestamp is empty.",
      fixHint: "Map a column that contains a date/time on trade rows, or filter out non-trade rows.",
    };
  }

  const format = datetimeFormat?.trim() ?? "";
  const timezoneAbbrev = trimmed.match(/\s([A-Z]{2,5})$/)?.[1];
  const hasTimezoneSuffix = Boolean(timezoneAbbrev);
  const hasNumericOffset = /[+-]\d{2}:?\d{2}$/.test(trimmed);
  const formatIncludesZone = /%z|%Z|%\+/.test(format);

  if (hasTimezoneSuffix && format && !formatIncludesZone) {
    return {
      ok: false,
      issue: `Value ends with timezone "${timezoneAbbrev}" but your date format has no timezone placeholder (%Z).`,
      fixHint:
        'On the Formats step, choose Other and set the format to include %Z — e.g. %Y-%m-%d %H:%M:%S %Z — or pick a format that matches the full string.',
    };
  }

  if (!format && (hasTimezoneSuffix || hasNumericOffset)) {
    return {
      ok: false,
      issue: "Value includes a timezone suffix; Auto/default format may not parse it.",
      fixHint:
        "On the Formats step, set an explicit date/time format that includes the timezone (e.g. %Z) or matches your file exactly.",
    };
  }

  if (format === "%Y-%m-%d %H:%M:%S" && !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(trimmed)) {
    return {
      ok: false,
      issue: `Value "${trimmed}" does not match format ${format}.`,
      fixHint: "Adjust the date/time format on the Formats step to match this value exactly.",
    };
  }

  if (format && !formatIncludesZone && hasTimezoneSuffix) {
    return {
      ok: false,
      issue: `Value "${trimmed}" includes extra text after the time.`,
      fixHint: "Extend your date format to cover the suffix (often %Z for abbreviations like EST).",
    };
  }

  return { ok: true };
}

export function diagnoseNumericValue(
  rawValue: string,
  fieldLabel: string,
  decimalSeparator: string | undefined
): { ok: boolean; issue?: string; fixHint?: string } {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return {
      ok: false,
      issue: `${fieldLabel} is empty.`,
      fixHint: `Map a column with ${fieldLabel.toLowerCase()} on trade rows, or filter out rows without fills.`,
    };
  }

  const sep = decimalSeparator === "," ? "," : ".";
  const otherSep = sep === "," ? "." : ",";
  let normalized = trimmed.replace(/\s/g, "");
  if (sep === ",") {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else if (otherSep === "," && normalized.includes(",")) {
    return {
      ok: false,
      issue: `${fieldLabel} "${trimmed}" uses comma as decimal separator.`,
      fixHint: 'On the Formats step, set Decimal separator to "Comma (1,25)".',
    };
  }

  if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
    return {
      ok: false,
      issue: `${fieldLabel} "${trimmed}" is not a valid number.`,
      fixHint: "Check the mapped column or decimal separator on the Formats step.",
    };
  }

  return { ok: true };
}

export function diagnoseSideValue(
  rawValue: string,
  sideMap: ValueTransformMap
): { ok: boolean; issue?: string; fixHint?: string } {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return {
      ok: false,
      issue: "Side is empty.",
      fixHint: "Map a column with buy/sell (or long/short) on trade rows.",
    };
  }

  const mapped = sideMap[trimmed]?.trim();
  if (Object.keys(sideMap).length > 0 && !mapped) {
    return {
      ok: false,
      issue: `Side value "${trimmed}" is not mapped to BUY or SELL.`,
      fixHint: "On the Formats step, add a mapping for this value under Map trade direction values.",
    };
  }

  if (Object.keys(sideMap).length === 0) {
    const upper = trimmed.toUpperCase();
    if (upper !== "BUY" && upper !== "SELL") {
      return {
        ok: false,
        issue: `Side value "${trimmed}" is not BUY or SELL and no direction mapping is configured.`,
        fixHint: "On the Formats step, map your file's direction labels to BUY and SELL.",
      };
    }
  }

  return { ok: true };
}

function diagnoseField(
  tesseraField: string,
  row: Record<string, string>,
  columnMapping: ColumnMapping,
  valueTransforms: ValueTransforms
): EnrichedPreviewError | null {
  const columnHeader = columnMapping[tesseraField]?.trim();
  if (!columnHeader) return null;

  const rawValue = String(row[columnHeader] ?? "");
  const tesseraFieldLabel = labelForTesseraField(tesseraField);
  let diagnosis: { ok: boolean; issue?: string; fixHint?: string };

  switch (tesseraField) {
    case "timestamp":
      diagnosis = diagnoseTimestampValue(rawValue, String(valueTransforms.datetime_format ?? ""));
      break;
    case "price":
      diagnosis = diagnoseNumericValue(
        rawValue,
        tesseraFieldLabel,
        String(valueTransforms.decimal_separator ?? ".")
      );
      break;
    case "quantity":
      diagnosis = diagnoseNumericValue(
        rawValue,
        tesseraFieldLabel,
        String(valueTransforms.decimal_separator ?? ".")
      );
      break;
    case "side":
      diagnosis = diagnoseSideValue(rawValue, getSideMap(valueTransforms));
      break;
    default:
      return null;
  }

  if (diagnosis.ok) return null;

  return {
    row: null,
    tesseraField,
    tesseraFieldLabel,
    columnHeader,
    rawValue: rawValue.trim() || "—",
    serverField: tesseraField,
    serverMessage: "",
    issue: diagnosis.issue ?? "Invalid value.",
    fixHint: diagnosis.fixHint,
  };
}

function csvRowForErrorRow(csvRows: Record<string, string>[], rowNumber: number | null) {
  if (rowNumber == null || rowNumber < 1) return null;
  return csvRows[rowNumber - 1] ?? null;
}

function fieldsToDiagnose(serverField: string | null, serverMessage: string): string[] {
  if (serverField?.trim()) return [serverField.trim()];
  if (VAGUE_ROW_VALUE_ERROR_RE.test(serverMessage)) return [...ROW_PARSE_FIELDS];
  return [...ROW_PARSE_FIELDS];
}

export function isPreciseServerError(err: CsvImportError): boolean {
  if (err.error_code?.trim()) return true;
  if (err.field?.trim() && !VAGUE_ROW_VALUE_ERROR_RE.test(err.message)) return true;
  return false;
}

function fixHintFromServerError(err: CsvImportError): string | undefined {
  if (err.raw_value?.trim() && err.field === "timestamp") {
    const inferred = resolveDatetimeFormatFix(err.raw_value, err.expected ?? undefined);
    if (inferred) {
      return `On the Formats step, choose: ${labelForDatetimeFormat(inferred, err.raw_value.trim())}`;
    }
  }
  if (err.expected?.trim()) {
    return `Expected: ${err.expected.trim()}`;
  }
  return undefined;
}

function mapServerError(err: CsvImportError, columnMapping: ColumnMapping): EnrichedPreviewError {
  const tesseraField = err.field?.trim() ?? "row";
  const columnHeader =
    err.column?.trim() ??
    (err.field ? columnMapping[err.field]?.trim() ?? "—" : "—");

  return {
    row: err.row,
    tesseraField,
    tesseraFieldLabel: err.field ? labelForTesseraField(err.field) : "Row",
    columnHeader,
    rawValue: err.raw_value?.trim() || "—",
    serverField: err.field,
    serverMessage: err.message,
    errorCode: err.error_code ?? null,
    expected: err.expected ?? null,
    issue: err.message,
    fixHint: fixHintFromServerError(err),
    fromServer: true,
  };
}

/** Prefer precise server errors; fall back to client-side diagnostics for legacy responses. */
export function enrichPreviewErrors(
  errors: CsvImportError[],
  options: {
    csvRows: Record<string, string>[];
    columnMapping: ColumnMapping;
    valueTransforms: ValueTransforms;
  }
): EnrichedPreviewError[] {
  const { csvRows, columnMapping, valueTransforms } = options;
  const enriched: EnrichedPreviewError[] = [];

  for (const err of errors) {
    if (isPreciseServerError(err)) {
      enriched.push(mapServerError(err, columnMapping));
      continue;
    }

    const row = csvRowForErrorRow(csvRows, err.row);
    const fields = fieldsToDiagnose(err.field, err.message);
    const rowFailures: EnrichedPreviewError[] = [];

    if (row) {
      for (const field of fields) {
        const diagnosis = diagnoseField(field, row, columnMapping, valueTransforms);
        if (diagnosis) {
          rowFailures.push({
            ...diagnosis,
            row: err.row,
            serverField: err.field,
            serverMessage: err.message,
          });
        }
      }
    }

    if (rowFailures.length > 0) {
      enriched.push(...rowFailures);
      continue;
    }

    enriched.push({
      row: err.row,
      tesseraField: err.field ?? "row",
      tesseraFieldLabel: err.field ? labelForTesseraField(err.field) : "Row",
      columnHeader: err.field ? columnMapping[err.field]?.trim() ?? "—" : "—",
      rawValue: row && err.field ? String(row[columnMapping[err.field] ?? ""] ?? "").trim() || "—" : "—",
      serverField: err.field,
      serverMessage: err.message,
      issue: err.message,
      fixHint:
        err.row != null && !row
          ? "This row is outside the rows loaded for diagnostics. Check the value in your CSV directly."
          : undefined,
    });
  }

  return enriched;
}

export function summarizePreviewErrors(errors: EnrichedPreviewError[]): PreviewErrorSummary[] {
  const groups = new Map<string, PreviewErrorSummary>();

  for (const err of errors) {
    const key = `${err.errorCode ?? err.tesseraField}::${err.issue}::${err.fixHint ?? ""}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      continue;
    }
    groups.set(key, {
      key,
      count: 1,
      tesseraField: err.tesseraField,
      tesseraFieldLabel: err.tesseraFieldLabel,
      issue: err.issue,
      fixHint: err.fixHint,
      exampleRow: err.row ?? 0,
      exampleColumn: err.columnHeader,
      exampleValue: err.rawValue,
    });
  }

  return Array.from(groups.values()).sort((a, b) => b.count - a.count);
}
